/**
 * Utilitaires pour l'export de données en différents formats
 * CSV et JSON supportés
 */

/**
 * Convertit un tableau d'objets en format CSV
 */
export const convertToCSV = <T extends Record<string, unknown>>(data: T[]): string => {
  if (data.length === 0) return '';

  const headers = Object.keys(data[0]);

  const headerLine = headers.map(escapeCSVValue).join(',');

  const dataLines = data.map((row) =>
    headers.map((header) => escapeCSVValue(row[header])).join(',')
  );

  return [headerLine, ...dataLines].join('\n');
};

/**
 * Échappe les valeurs pour le format CSV
 */
const escapeCSVValue = (value: unknown): string => {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);

  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

/**
 * Télécharge des données au format CSV
 */
export const downloadCSV = <T extends Record<string, unknown>>(
  data: T[],
  filename: string
): void => {
  const csv = convertToCSV(data);
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, `${filename}.csv`);
};

/**
 * Télécharge des données au format JSON
 */
export const downloadJSON = <T extends Record<string, unknown>>(
  data: T[],
  filename: string
): void => {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
  downloadBlob(blob, `${filename}.json`);
};

/**
 * Télécharge un blob avec un nom de fichier donné
 */
const downloadBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Formatte les données pour l'export en nettoyant les champs inutiles
 */
export const prepareDataForExport = <T extends Record<string, unknown>>(
  data: T[],
  fieldsToExclude: string[] = []
): Partial<T>[] => {
  return data.map((item) => {
    const cleaned: Record<string, unknown> = { ...item };

    fieldsToExclude.forEach((field) => {
      delete cleaned[field];
    });

    Object.keys(cleaned).forEach((key) => {
      const value = cleaned[key];
      if (value instanceof Date) {
        cleaned[key] = value.toISOString();
      } else if (typeof value === 'string' && isISODate(value)) {
        cleaned[key] = new Date(value).toLocaleDateString('fr-FR');
      }
    });

    return cleaned as Partial<T>;
  });
};

/**
 * Vérifie si une chaîne est une date ISO
 */
const isISODate = (str: string): boolean => {
  const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
  return isoDateRegex.test(str);
};

/**
 * Parse un fichier CSV et retourne un tableau d'objets
 */
export const parseCSV = (csvText: string): Record<string, string>[] => {
  const lines = csvText.split('\n').filter((line) => line.trim());
  if (lines.length === 0) return [];

  const headers = lines[0].split(',').map((h) => h.trim());

  return lines.slice(1).map((line) => {
    const values = line.split(',').map((v) => v.trim());
    const obj: Record<string, string> = {};

    headers.forEach((header, index) => {
      obj[header] = values[index] || '';
    });

    return obj;
  });
};

/**
 * Lit un fichier et retourne son contenu en tant que texte
 */
export const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = (e) => reject(e);
    reader.readAsText(file);
  });
};
